import React, { useState, useCallback, useEffect } from 'react'
import { useStore, useSettings, useEffectsState, usePresets } from '../../state/store'
import type { Preset } from '../../core/presets/types'
import { getPresetManager } from '../../core/presets/PresetManager'
import { BUILTIN_PRESETS } from '../../core/presets/defaultPresets'

const PresetPanel: React.FC = () => {
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const [presetName, setPresetName] = useState('')
  const [presetDescription, setPresetDescription] = useState('')
  const [activeTab, setActiveTab] = useState<'builtin' | 'user'>('builtin')
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const settings = useSettings()
  const effectsState = useEffectsState()
  const userPresets = usePresets()
  const { addPreset, removePreset, updateSettings, updateEffectsState } = useStore()

  useEffect(() => {
    getPresetManager().registerBuiltinPresets(BUILTIN_PRESETS)
  }, [])

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 3000)
  }, [])

  const handleSavePreset = useCallback(() => {
    if (!presetName.trim()) {
      showNotification('error', '请输入预设名称')
      return
    }

    const newPreset = getPresetManager().createPreset(presetName.trim(), settings, effectsState, presetDescription.trim())

    if (userPresets.some((p) => p.name === newPreset.name)) {
      showNotification('error', '预设名称已存在')
      return
    }

    addPreset(newPreset)
    showNotification('success', `预设“${newPreset.name}”已保存`)
    setIsSaveModalOpen(false)
    setPresetName('')
    setPresetDescription('')
  }, [presetName, presetDescription, settings, effectsState, userPresets, addPreset, showNotification])

  const handleLoadPreset = useCallback((preset: Preset) => {
    updateSettings(preset.settings)
    updateEffectsState(preset.effectsState)
    showNotification('success', `已加载预设“${preset.name}”`)
  }, [updateSettings, updateEffectsState, showNotification])

  const handleDeletePreset = useCallback((presetId: string, presetNameValue: string) => {
    if (window.confirm(`确定要删除预设“${presetNameValue}”吗？`)) {
      removePreset(presetId)
      showNotification('success', `预设“${presetNameValue}”已删除`)
    }
  }, [removePreset, showNotification])

  const currentPresets = activeTab === 'builtin' ? BUILTIN_PRESETS : userPresets

  return (
    <div className="panel-section">
      <div className="xp-section-header">
        <h3 className="panel-title">预设管理</h3>
      </div>

      <div className="xp-section-body">
        {notification && (
          <div className={notification.type === 'success' ? 'xp-success mb-3' : 'xp-error mb-3'}>
            {notification.message}
          </div>
        )}

        <div className="xp-tab-strip">
          <button onClick={() => setActiveTab('builtin')} className={`xp-tab ${activeTab === 'builtin' ? 'xp-tab-active' : ''}`}>
            内置预设
          </button>
          <button onClick={() => setActiveTab('user')} className={`xp-tab ${activeTab === 'user' ? 'xp-tab-active' : ''}`}>
            我的预设 ({userPresets.length})
          </button>
        </div>

        <div className="border border-[#8d8a83] border-t-0 bg-[#efece2] p-2 max-h-60 overflow-y-auto">
          {currentPresets.length === 0 ? (
            <div className="xp-note">
              {activeTab === 'user' ? '暂无自定义预设。' : '暂无内置预设。'}
            </div>
          ) : (
            <div className="space-y-2">
              {currentPresets.map((preset) => (
                <div key={preset.id} className="xp-list-item">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[16px] font-bold truncate">{preset.name}</div>
                      {preset.description && (
                        <div className="xp-muted text-[15px] mt-1 truncate">{preset.description}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleLoadPreset(preset)} className="xp-btn xp-btn-small">应用</button>
                      {preset.category === 'user' && (
                        <button onClick={() => handleDeletePreset(preset.id, preset.name)} className="xp-btn xp-btn-small">删除</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <button onClick={() => setIsSaveModalOpen(true)} className="btn-primary w-full mt-3">
          保存当前设置为预设
        </button>
      </div>

      {isSaveModalOpen && (
        <div className="xp-modal-backdrop">
          <div className="xp-window xp-dialog">
            <div className="xp-title-bar">
              <span className="xp-title-text">保存预设</span>
              <button
                onClick={() => {
                  setIsSaveModalOpen(false)
                  setPresetName('')
                  setPresetDescription('')
                }}
                className="xp-title-button"
              >
                ×
              </button>
            </div>
            <div className="xp-dialog-body space-y-3">
              <div>
                <label className="xp-group-label" htmlFor="preset-name">预设名称</label>
                <input
                  id="preset-name"
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="输入预设名称"
                  className="xp-input"
                  maxLength={30}
                />
              </div>
              <div>
                <label className="xp-group-label" htmlFor="preset-description">预设说明</label>
                <textarea
                  id="preset-description"
                  value={presetDescription}
                  onChange={(e) => setPresetDescription(e.target.value)}
                  placeholder="输入预设说明"
                  className="xp-textarea w-full resize-none"
                  rows={3}
                  maxLength={100}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => {
                    setIsSaveModalOpen(false)
                    setPresetName('')
                    setPresetDescription('')
                  }}
                  className="btn-secondary"
                >
                  取消
                </button>
                <button onClick={handleSavePreset} className="btn-primary">保存</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PresetPanel
